import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowRight, RefreshCw } from 'lucide-react';

export default function VerifyResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);
  
  const status = searchParams.get('status');
  const message = searchParams.get('message');
  
  const isSuccess = status === 'success';
  
  // Map error messages to user-friendly text
  const getErrorMessage = () => {
    switch (message) {
      case 'invalid':
        return 'Invalid verification link. The link may have already been used or is incorrect.';
      case 'expired':
        return 'This verification link has expired. Please register again to get a new link.';
      case 'already_registered':
        return 'This email is already registered. You can login to your account.';
      case 'server_error':
        return 'Something went wrong. Please try again later.';
      default:
        return 'Verification failed. Please try again or contact support.';
    }
  };

  // Auto redirect on success
  useEffect(() => {
    if (isSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (isSuccess && countdown === 0) {
      navigate('/login?verified=true');
    }
  }, [isSuccess, countdown, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-slide-in">
          <div className="w-24 h-24 mx-auto mb-3 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/logo-new.png" 
              alt="GuestCheckout" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Result Card */}
        <div className="card-3d p-8 text-center animate-slide-in" style={{ animationDelay: '0.1s' }}>
          {isSuccess ? (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3" data-testid="verify-success-title">
                Email Verified!
              </h2>
              <p className="text-slate-600 mb-4">
                Your account has been activated successfully. You can now login.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Redirecting to login in {countdown}...
              </p>
              <button
                onClick={() => navigate('/login?verified=true')}
                className="btn-3d btn-3d-primary w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                data-testid="go-to-login-btn"
              >
                Go to Login
                <ArrowRight size={18} />
              </button>
            </>
          ) : (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
                <XCircle size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3" data-testid="verify-error-title">
                Verification Failed
              </h2>
              <p className="text-slate-600 mb-6" data-testid="verify-error-message">
                {getErrorMessage()}
              </p>
              <div className="space-y-3">
                {message === 'already_registered' ? (
                  <button
                    onClick={() => navigate('/login')}
                    className="btn-3d btn-3d-primary w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                    data-testid="go-to-login-btn"
                  >
                    Go to Login
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/register')}
                      className="btn-3d btn-3d-primary w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                      data-testid="register-again-btn"
                    >
                      Register Again
                      <ArrowRight size={18} />
                    </button>
                    <button
                      onClick={() => navigate('/login')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      data-testid="back-to-login-link"
                    >
                      Back to Login
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} GuestCheckoutapp. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
